"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ProjectContext = void 0;
exports.default = Project;
exports.useProjectNavigation = void 0;
var _react = require("react");
var _Page = _interopRequireDefault(require("./Page"));
var _axios = _interopRequireDefault(require("axios"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
// Contexte pour la navigation entre pages
const ProjectContext = exports.ProjectContext = /*#__PURE__*/(0, _react.createContext)(null);

// Hook pour utiliser la navigation
const useProjectNavigation = () => {
  const context = (0, _react.useContext)(ProjectContext);
  if (!context) {
    throw new Error('useProjectNavigation must be used within a Project component');
  }
  return context;
};
exports.useProjectNavigation = useProjectNavigation;
function Project({
  project,
  screenSize,
  initialPageId = null,
  apiUri = 'https://kiosk-pluracore-60922b42b03b.herokuapp.com',
  device_id,
  uri_transformer = url => url
}) {
  // Trouver la page initiale
  const getInitialPage = () => {
    if (initialPageId) {
      return project?.pages?.find(p => p.id === initialPageId) || project?.pages?.[0];
    } else {
      if (project?.main_page_id) {
        return project?.pages?.find(p => p.id === project?.main_page_id) || project?.pages?.[0];
      } else {
        return project?.pages?.[0];
      }
    }
  };
  const [displayedPage, setDisplayedPage] = (0, _react.useState)(getInitialPage);
  const [outgoingPage, setOutgoingPage] = (0, _react.useState)(null);
  const [outgoingOpacity, setOutgoingOpacity] = (0, _react.useState)(1);
  const isTransitioning = (0, _react.useRef)(false);

  // Durée de la transition en ms
  const TRANSITION_DURATION = 400;

  // Navigation vers une page spécifique
  const navigateToPage = (0, _react.useCallback)(pageId => {
    if (isTransitioning.current) return;
    const targetPage = project?.pages?.find(p => p.id === pageId);
    if (!targetPage || targetPage.id === displayedPage?.id) return;
    isTransitioning.current = true;

    // 1. Préparer l'ancienne page comme "outgoing" avec opacity 1
    setOutgoingPage(displayedPage);
    setOutgoingOpacity(1);

    // 2. La nouvelle page est immédiatement affichée en dessous
    setDisplayedPage(targetPage);

    // 3. Déclencher le fade out après que le DOM soit mis à jour
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setOutgoingOpacity(0);
      });
    });

    // 4. Nettoyer après la transition
    setTimeout(() => {
      setOutgoingPage(null);
      setOutgoingOpacity(1);
      isTransitioning.current = false;
    }, TRANSITION_DURATION + 100);
  }, [project?.pages, displayedPage]);
  const logEvent = async event => {
    if (!apiUri) {
      console.warn('logEvent: apiUri is not defined', apiUri);
      return;
    }
    try {
      fetch(`${apiUri}/trace-logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...event,
          device_id,
          page_id: displayedPage?.id,
          project_id: project.id
        })
      });
    } catch (error) {
      console.error('logEvent: error', error);
    }
  };

  // Navigation vers la page suivante
  const goToNextPage = (0, _react.useCallback)(() => {
    if (!project?.pages?.length) return;
    const currentIndex = project.pages.findIndex(p => p.id === displayedPage?.id);
    const nextIndex = (currentIndex + 1) % project.pages.length;
    navigateToPage(project.pages[nextIndex].id);
  }, [project?.pages, displayedPage, navigateToPage]);

  // Navigation vers la page précédente
  const goToPreviousPage = (0, _react.useCallback)(() => {
    if (!project?.pages?.length) return;
    const currentIndex = project.pages.findIndex(p => p.id === displayedPage?.id);
    const prevIndex = currentIndex === 0 ? project.pages.length - 1 : currentIndex - 1;
    navigateToPage(project.pages[prevIndex].id);
  }, [project?.pages, displayedPage, navigateToPage]);

  // Valeur du contexte
  const contextValue = {
    currentPage: displayedPage,
    pages: project?.pages || [],
    navigateToPage,
    nextPage: goToNextPage,
    previousPage: goToPreviousPage,
    isTransitioning: isTransitioning.current
  };
  if (!displayedPage) {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      style: {
        width: screenSize?.width,
        height: screenSize?.height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1a1a2e',
        color: '#64748b'
      },
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        children: "Aucune page dans ce projet"
      })
    });
  }
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(ProjectContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      style: {
        width: screenSize?.width,
        height: screenSize?.height,
        position: 'relative',
        overflow: 'hidden'
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: {
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1
        },
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Page.default, {
          page: displayedPage,
          screenSize: screenSize,
          project: project,
          logEvent: logEvent,
          device_id: device_id,
          project_id: project.id,
          uri_transformer: uri_transformer
        })
      }), outgoingPage && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        style: {
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 2,
          opacity: outgoingOpacity,
          transition: `opacity ${TRANSITION_DURATION}ms ease-in-out`,
          pointerEvents: 'none'
        },
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Page.default, {
          page: outgoingPage,
          screenSize: screenSize,
          project: project,
          logEvent: logEvent,
          device_id: device_id,
          project_id: project.id,
          uri_transformer: uri_transformer
        })
      })]
    })
  });
}